import { Badge, Group, Text } from '@mantine/core';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { ClientListItem } from 'src/admin/users/types';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { NameCell } from 'src/common/components/table/NameCell';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { SortableColumn } from 'src/common/components/table/types';
import { ClientActionsMenu } from './components/ClientActionsMenu';
import {
  AdvisorFilterSelect,
  getAdvisorSortValue,
  matchesAdvisorFilter,
  matchesAdvisorSearch,
  NO_ADVISOR_FILTER_VALUE,
} from './components/AdvisorFilterSelect';
import { FilterRow, ViewMode } from './components/FilterRow';
import { useAdvisors } from './hooks/useAdvisors';
import { useDeleteClient } from './hooks/useDeleteClient';
import { formatName } from 'src/utils/nameFormatting';
import { matchesClientSearch } from 'src/utils/clientSearch';
import { useFlags } from 'src/feature-flags/FlagsHook';

export function ClientsTable({
  title = 'Clients',
  titleIcon,
  onTitleClick,
  filters,
  showViewModes = true,
  paginated = false,
  primaryButton,
}: {
  title?: string;
  titleIcon?: React.ReactNode;
  onTitleClick?: () => void;
  filters?: Record<string, string>;
  showViewModes?: boolean;
  paginated?: boolean;
  primaryButton?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'primary';
  };
}) {
  const [viewMode, setViewMode] = useState<ViewMode>('ViewAll');
  const [advisorFilter, setAdvisorFilter] = useState<string | null>(null);
  const [clientToDelete, setClientToDelete] = useState<ClientListItem | null>(null);
  const { advisors } = useAdvisors();
  const {
    deleteClient,
    error: deletionError,
    reset: resetDeleteClient,
    isLoading: isDeletingClient,
  } = useDeleteClient();
  const { isSystemAdmin, isServiceAdmin, isAdvisor } = useCurrentUser();
  const { flagEnabledAdvisorSortAndFilter, flagEnabledClientSearchFullName } = useFlags();
  const navigate = useNavigate();

  const advisorMap = useMemo(() => {
    if (!advisors) return {};

    return Object.fromEntries(advisors.map(advisor => [advisor.id, advisor]));
  }, [advisors]);

  const handleDeletePress = (client: ClientListItem) => {
    resetDeleteClient();
    setClientToDelete(client);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    await deleteClient(clientToDelete.id);
    setClientToDelete(null);
  };

  const columns: SortableColumn<ClientListItem>[] = [
    {
      accessor: 'name',
      title: 'Name',
      sortable: true,
      sortKey: 'lastName',
      render: (client: ClientListItem) => <NameCell entity={client} />,
    },
    {
      accessor: 'advised',
      title: 'Groups',
      render: (client: ClientListItem) => {
        const family = client.family;
        return (
          <Group>
            <Badge variant={client.advised ? 'brand' : 'pink'}>
              {client.advised ? 'Advised' : 'Non-advised'}
            </Badge>
            {family != null && (
              <Badge
                variant="green-clickable"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/clients/families/${family.id}`);
                }}
              >{`${family.name} Family`}</Badge>
            )}
          </Group>
        );
      },
    },
    {
      accessor: 'createdAt',
      title: 'Date added',
      sortable: true,
      sortKey: 'createdAt',
      render: (client: ClientListItem) => {
        const date = new Date(client.createdAt);
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        return (
          <Text size="sm" c="gray.7">
            {formattedDate}
          </Text>
        );
      },
    },
    {
      accessor: 'actions',
      title: '',
      render: (client: ClientListItem) => (
        <ClientActionsMenu
          client={client}
          onDelete={() => handleDeletePress(client)}
          iconSize={16}
        />
      ),
    },
  ];

  if (isSystemAdmin || isServiceAdmin) {
    columns.splice(2, 0, {
      accessor: 'advisor',
      title: 'Adviser',
      ...(flagEnabledAdvisorSortAndFilter && {
        sortable: true,
        sortIconType: 'alpha',
        sortValueGetter: (client: ClientListItem) =>
          getAdvisorSortValue(client.advisorUserId, advisorMap),
      }),
      render: (client: ClientListItem) => {
        const advisorUserId = client.advisorUserId;
        if (!advisorUserId)
          return (
            <Text size="sm" c="gray.7">
              No adviser
            </Text>
          );
        const advisor = advisorMap[advisorUserId];
        const advisorName = advisor
          ? formatName(advisor.firstName, advisor.lastName)
          : 'No adviser';
        return (
          <Text size="sm" c="gray.7">
            {advisorName}
          </Text>
        );
      },
    });
  }

  const showAdvisorFilter = (isSystemAdmin || isServiceAdmin) && flagEnabledAdvisorSortAndFilter;

  const filterPredicate = (client: ClientListItem, search: string) => {
    if (viewMode === 'ViewAdvised' && !client.advised) {
      return false;
    }

    if (viewMode === 'ViewNonAdvised' && client.advised) {
      return false;
    }

    if (!matchesAdvisorFilter(client.advisorUserId, advisorFilter)) {
      return false;
    }

    if (
      matchesClientSearch(
        [client.firstName, client.lastName, client.emailAddress],
        search,
        flagEnabledClientSearchFullName,
      )
    ) {
      return true;
    }

    if (
      !isAdvisor &&
      flagEnabledAdvisorSortAndFilter &&
      matchesAdvisorSearch(client.advisorUserId, advisorMap, search.toLowerCase())
    ) {
      return true;
    }

    return false;
  };

  const serverFilters = {
    ...filters,
    ...(viewMode === 'ViewAdvised' && { advised: true }),
    ...(viewMode === 'ViewNonAdvised' && { advised: false }),
    ...(advisorFilter != null &&
      advisorFilter !== NO_ADVISOR_FILTER_VALUE && { advisorUserId: advisorFilter }),
  };

  return (
    <>
      <SimpleTable<ClientListItem>
        title={title}
        titleIcon={titleIcon}
        onTitleClick={onTitleClick}
        resource="clients"
        tableProps={{
          variant: 'clients-table',
        }}
        columns={columns}
        {...(paginated
          ? {
              mode: 'server' as const,
              serverSearch: true,
              filters: serverFilters,
              initialSort: { columnAccessor: 'name', direction: 'asc' as const },
            }
          : {
              filters,
              filterPredicate,
              paginationDependencies: `${viewMode}-${advisorFilter}`,
            })}
        onRowClick={client => {
          navigate(`/client/${client.id}/non-financial-data/personal-details`);
        }}
        HeaderRow={
          (showViewModes || showAdvisorFilter) && (
            <>
              {showViewModes && <FilterRow viewMode={viewMode} setViewMode={setViewMode} />}
              {showAdvisorFilter && (
                <AdvisorFilterSelect
                  advisors={advisors}
                  value={advisorFilter}
                  onChange={setAdvisorFilter}
                />
              )}
            </>
          )
        }
        primaryButton={primaryButton}
      />
      <ConfirmDeleteModal
        opened={!!clientToDelete}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete client"
        message={`Are you sure you want to delete ${formatName(clientToDelete?.firstName, clientToDelete?.lastName)}? This action cannot be undone.`}
        error={deletionError}
        isLoading={isDeletingClient}
      />
    </>
  );
}
